import { useEffect, useState } from 'react'
import api from '../../services/api'
import {
  Globe, Plus, Key, Building2, Users, Trash2, Edit3, Eye,
  EyeOff, Copy, Check, Clock, AlertTriangle, X, ExternalLink,
  Shield, Link
} from 'lucide-react'

const ORG_TYPES = [
  { value: 'contractor', label: 'Contractor' },
  { value: 'client', label: 'Client' },
  { value: 'principal', label: 'Principal Contractor' },
  { value: 'regulator', label: 'Regulator' },
  { value: 'insurer', label: 'Insurer' },
  { value: 'auditor', label: 'Auditor' },
]

const SCOPES = [
  { key: 'incidents:read', label: 'View Incidents' },
  { key: 'incidents:create', label: 'Log Incidents' },
  { key: 'audits:read', label: 'View Audits' },
  { key: 'audits:conduct', label: 'Conduct Audits' },
  { key: 'permits:read', label: 'View Permits' },
  { key: 'permits:create', label: 'Create Permits' },
  { key: 'actions:read', label: 'View Corrective Actions' },
  { key: 'actions:create', label: 'Log Actions' },
  { key: 'workforce:read', label: 'View Workforce' },
  { key: 'competencies:read', label: 'View Competencies' },
  { key: 'reports:view', label: 'View Reports' },
]

type ExternalOrg = {
  id: string; name: string; type: string; abn?: string
  contact_name?: string; contact_email?: string; contact_phone?: string
  is_active: number; user_count: number; grant_count: number
}

type AccessGrant = {
  id: string; name: string; email: string; scope: string[]
  org_name?: string; expires_at?: string; last_accessed_at?: string
  access_count: number; is_active: number; token: string; created_at: string
  granted_by_name: string
}

type ContractorUser = {
  id: string; name: string; email: string; role: string
  org_name?: string; is_active: number; accepted_at?: string; invite_sent_at: string
}

export default function ExternalAccessPage() {
  const [tab, setTab] = useState<'orgs' | 'grants' | 'users'>('orgs')
  const [orgs, setOrgs] = useState<ExternalOrg[]>([])
  const [grants, setGrants] = useState<AccessGrant[]>([])
  const [contractorUsers, setContractorUsers] = useState<ContractorUser[]>([])
  const [loading, setLoading] = useState(true)

  const [showOrgForm, setShowOrgForm] = useState(false)
  const [showGrantForm, setShowGrantForm] = useState(false)
  const [showUserForm, setShowUserForm] = useState(false)
  const [orgForm, setOrgForm] = useState({ name: '', type: 'contractor', abn: '', contact_name: '', contact_email: '', contact_phone: '' })
  const [grantForm, setGrantForm] = useState({ name: '', email: '', external_org_id: '', scope: [] as string[], expires_days: '' })
  const [userForm, setUserForm] = useState({ name: '', email: '', role: 'worker', external_org_id: '' })
  const [saving, setSaving] = useState(false)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  useEffect(() => {
    loadAll()
  }, [])

  async function loadAll() {
    setLoading(true)
    try {
      const [orgsRes, grantsRes, usersRes] = await Promise.all([
        api.get('/external/organisations'),
        api.get('/external/grants'),
        api.get('/external/contractor-users'),
      ])
      setOrgs(orgsRes.data.data || [])
      setGrants(grantsRes.data.data || [])
      setContractorUsers(usersRes.data.data || [])
    } catch {}
    setLoading(false)
  }

  async function createOrg() {
    if (!orgForm.name) return
    setSaving(true)
    try {
      const r = await api.post('/external/organisations', orgForm)
      setOrgs(o => [...o, r.data])
      setShowOrgForm(false)
      setOrgForm({ name: '', type: 'contractor', abn: '', contact_name: '', contact_email: '', contact_phone: '' })
    } catch (e: any) { alert(e.response?.data?.error || 'Failed') }
    setSaving(false)
  }

  async function createGrant() {
    if (!grantForm.email || !grantForm.name) return
    setSaving(true)
    try {
      const r = await api.post('/external/grants', { ...grantForm, expires_days: grantForm.expires_days || undefined })
      setGrants(g => [r.data, ...g])
      setShowGrantForm(false)
      setGrantForm({ name: '', email: '', external_org_id: '', scope: [], expires_days: '' })
    } catch (e: any) { alert(e.response?.data?.error || 'Failed') }
    setSaving(false)
  }

  async function createUser() {
    if (!userForm.email || !userForm.name) return
    setSaving(true)
    try {
      const r = await api.post('/external/contractor-users', userForm)
      setContractorUsers(u => [r.data, ...u])
      setShowUserForm(false)
      setUserForm({ name: '', email: '', role: 'worker', external_org_id: '' })
    } catch (e: any) { alert(e.response?.data?.error || 'Failed') }
    setSaving(false)
  }

  async function revokeGrant(id: string) {
    if (!confirm('Revoke this access grant?')) return
    await api.delete(`/external/grants/${id}`)
    setGrants(g => g.map(gg => gg.id === id ? { ...gg, is_active: 0 } : gg))
  }

  function copyToken(token: string, grantId: string) {
    const url = `${window.location.origin}/upload/${token}`
    navigator.clipboard.writeText(url)
    setCopiedToken(grantId)
    setTimeout(() => setCopiedToken(null), 2000)
  }

  const TYPE_BADGES: Record<string, string> = {
    contractor: 'bg-blue-100 text-blue-700',
    client: 'bg-green-100 text-green-700',
    principal: 'bg-purple-100 text-purple-700',
    regulator: 'bg-red-100 text-red-700',
    insurer: 'bg-amber-100 text-amber-700',
    auditor: 'bg-gray-100 text-gray-700',
  }

  if (loading) return <div className="p-8 text-gray-500">Loading...</div>

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe size={24} className="text-green-600" /> External Access
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage access for contractors, clients, regulators, and auditors</p>
        </div>
        <div className="flex gap-2">
          {tab === 'orgs' && (
            <button onClick={() => setShowOrgForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={15} /> Add Organisation
            </button>
          )}
          {tab === 'grants' && (
            <button onClick={() => setShowGrantForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Key size={15} /> Create Access Grant
            </button>
          )}
          {tab === 'users' && (
            <button onClick={() => setShowUserForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={15} /> Invite Contractor User
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {[
          { key: 'orgs', label: 'Organisations', icon: Building2, count: orgs.length },
          { key: 'grants', label: 'Access Grants', icon: Key, count: grants.filter(g => g.is_active).length },
          { key: 'users', label: 'Contractor Users', icon: Users, count: contractorUsers.filter(u => u.is_active).length },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
            <t.icon size={14} />
            {t.label}
            <span className="text-xs bg-gray-200 text-gray-600 rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Organisations */}
      {tab === 'orgs' && (
        <div className="space-y-3">
          {orgs.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500">
              No external organisations yet. Add contractors, clients, or regulators.
            </div>
          )}
          {orgs.map(org => (
            <div key={org.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                    <Building2 size={18} className="text-gray-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{org.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${TYPE_BADGES[org.type] || 'bg-gray-100 text-gray-700'}`}>
                        {ORG_TYPES.find(t => t.value === org.type)?.label || org.type}
                      </span>
                      {!org.is_active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Inactive</span>}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {org.abn && <span className="mr-3">ABN: {org.abn}</span>}
                      {org.contact_name && <span className="mr-3">{org.contact_name}</span>}
                      {org.contact_email && <span>{org.contact_email}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Users size={13} /> {org.user_count} users</span>
                  <span className="flex items-center gap-1"><Key size={13} /> {org.grant_count} grants</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Access Grants */}
      {tab === 'grants' && (
        <div className="space-y-3">
          {grants.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500">
              No access grants yet. Create a grant to give external parties scoped read/write access.
            </div>
          )}
          {grants.map(grant => {
            const expired = grant.expires_at && new Date(grant.expires_at) < new Date()
            return (
              <div key={grant.id} className={`bg-white border rounded-xl p-4 ${!grant.is_active || expired ? 'opacity-60 border-gray-200' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Key size={15} className="text-blue-500" />
                      <span className="font-semibold text-gray-900">{grant.name}</span>
                      {grant.org_name && <span className="text-xs text-gray-500">— {grant.org_name}</span>}
                      {!grant.is_active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Revoked</span>}
                      {expired && grant.is_active && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Clock size={10} /> Expired</span>}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">{grant.email} · Granted by {grant.granted_by_name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{grant.access_count} accesses</span>
                    {grant.is_active && !expired && (
                      <button onClick={() => copyToken(grant.token, grant.id)}
                        className="flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 transition-colors">
                        {copiedToken === grant.id ? <Check size={11} className="text-green-600" /> : <Copy size={11} />}
                        {copiedToken === grant.id ? 'Copied!' : 'Copy Link'}
                      </button>
                    )}
                    {grant.is_active && (
                      <button onClick={() => revokeGrant(grant.id)}
                        className="text-xs px-2 py-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors">
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(grant.scope || []).map(s => {
                    const scopeDef = SCOPES.find(sc => sc.key === s)
                    return (
                      <span key={s} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                        {scopeDef?.label || s}
                      </span>
                    )
                  })}
                </div>
                {grant.expires_at && (
                  <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <Clock size={10} /> Expires {new Date(grant.expires_at).toLocaleDateString('en-AU')}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Contractor Users */}
      {tab === 'users' && (
        <div className="space-y-3">
          {contractorUsers.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500">
              No contractor users yet.
            </div>
          )}
          {contractorUsers.map(user => (
            <div key={user.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 text-sm font-semibold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 text-sm">{user.name}</span>
                    {user.org_name && <span className="text-xs text-gray-500">— {user.org_name}</span>}
                    <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded capitalize">{user.role}</span>
                  </div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
              </div>
              <div className="text-xs text-gray-400">
                {user.accepted_at
                  ? <span className="text-green-600 flex items-center gap-1"><Check size={11} /> Accepted</span>
                  : <span className="flex items-center gap-1"><Clock size={11} /> Invite sent {new Date(user.invite_sent_at).toLocaleDateString('en-AU')}</span>
                }
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Organisation Form Modal */}
      {showOrgForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-semibold text-gray-900">Add External Organisation</h3>
              <button onClick={() => setShowOrgForm(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Organisation Name *</label>
                <input value={orgForm.name} onChange={e => setOrgForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="ABC Contractors Pty Ltd" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select value={orgForm.type} onChange={e => setOrgForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {ORG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">ABN</label>
                  <input value={orgForm.abn} onChange={e => setOrgForm(f => ({ ...f, abn: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="12 345 678 901" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Contact Name</label>
                  <input value={orgForm.contact_name} onChange={e => setOrgForm(f => ({ ...f, contact_name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Contact Email</label>
                  <input type="email" value={orgForm.contact_email} onChange={e => setOrgForm(f => ({ ...f, contact_email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                  <input value={orgForm.contact_phone} onChange={e => setOrgForm(f => ({ ...f, contact_phone: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex gap-3">
              <button onClick={() => setShowOrgForm(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={createOrg} disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Add Organisation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Form Modal */}
      {showGrantForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-semibold text-gray-900">Create Access Grant</h3>
              <button onClick={() => setShowGrantForm(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-start gap-2">
                <Link size={14} className="mt-0.5 shrink-0" />
                Creates a secure link that gives external parties read/write access to specific data without a full account.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Grant Name *</label>
                  <input value={grantForm.name} onChange={e => setGrantForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Site Audit Access" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Recipient Email *</label>
                  <input type="email" value={grantForm.email} onChange={e => setGrantForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Organisation</label>
                  <select value={grantForm.external_org_id} onChange={e => setGrantForm(f => ({ ...f, external_org_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">None</option>
                    {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Expires (days)</label>
                  <input type="number" value={grantForm.expires_days} onChange={e => setGrantForm(f => ({ ...f, expires_days: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="30" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Access Scope</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {SCOPES.map(s => (
                    <label key={s.key} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer py-1">
                      <input type="checkbox" checked={grantForm.scope.includes(s.key)}
                        onChange={() => setGrantForm(f => ({
                          ...f, scope: f.scope.includes(s.key) ? f.scope.filter(k => k !== s.key) : [...f.scope, s.key]
                        }))}
                        className="rounded border-gray-300 text-blue-600" />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex gap-3">
              <button onClick={() => setShowGrantForm(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={createGrant} disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Create Grant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contractor User Form Modal */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-semibold text-gray-900">Invite Contractor User</h3>
              <button onClick={() => setShowUserForm(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                <input value={userForm.name} onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                  <select value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="viewer">Viewer</option>
                    <option value="worker">Worker</option>
                    <option value="supervisor">Supervisor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Organisation</label>
                  <select value={userForm.external_org_id} onChange={e => setUserForm(f => ({ ...f, external_org_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">None</option>
                    {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex gap-3">
              <button onClick={() => setShowUserForm(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={createUser} disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
